import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import type { MutationDeleteModuleArgs } from "@/types/graphql";

/**
 * Custom hook to delete a module.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{deleteModule: Function, deleting: boolean}} - The function to delete a module and the loading state.
 */
export function useDeleteModule(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const [deleteModule, { loading }] = useMutation<
    any,
    MutationDeleteModuleArgs
  >(mutations.deleteModule, {
    onCompleted,
    onError: (err) => {
      onError(err);

      enqueueSnackbar("Delete Module: " + err.message, {
        variant: "error",
      });
    },
    errorPolicy: "all",
  });

  const deleteModuleRow = useCallback(
    (variables: MutationDeleteModuleArgs) => {
      deleteModule({
        variables,
        refetchQueries: ["getAllModules"],
      }).then();
    },
    [deleteModule],
  );

  return {
    deleteModule: deleteModuleRow,
    deleting: loading,
  };
}
